import Animated, {
  useAnimatedStyle,
  useDerivedValue,
} from 'react-native-reanimated';
import { ReactNode, useState } from 'react';
import { useWindowDimensions, View } from 'react-native';
import { useCurrentLayout } from '~/global/hooks/useCurrentLayout';
import {
  SidebarLayoutProvider,
  useSidebarLayout,
} from '~/layout/contexts/sidebar-layout/SidebarLayout';
import {
  RightSidebarLayoutProvider,
  useRightSidebar,
} from '~/layout/contexts/right-sidebar-layout/RightSidebarLayout';
import { sidebarConfig } from '~/layout/defs/sidebarConfig';
import { rightSidebarConfig } from '~/layout/defs/rightSidebarConfig';
import { Sidebar } from '~/layout/components/sidebar/Sidebar';
import { RightSidebar } from '~/layout/components/right-sidebar/RightSidebar';
import { Button } from '@/components/ui/button';
import { Icon } from '@/features/global/components/icon/Icon';
import { useColor } from '~/global/hooks/useColor';

export interface SidebarLayoutProps {
  children?: ReactNode;
}

const _SidebarLayout = ({ children }: SidebarLayoutProps) => {
  const { expandedState, toggleSidebar } = useSidebarLayout();
  const { railVisibleState, panelExpandedState, panelWidthValue } =
    useRightSidebar();
  const { width: screenWidth } = useWindowDimensions();
  const [isHovered, setIsHovered] = useState(false);
  const mutedColor = useColor('text-muted');
  const secondaryColor = useColor('text-secondary');
  const iconColor = isHovered ? mutedColor : secondaryColor;
  const sidebarWidth = useDerivedValue(
    () =>
      expandedState.value *
        (sidebarConfig.dimensions.open - sidebarConfig.dimensions.collapsed) +
      sidebarConfig.dimensions.collapsed,
    [],
  );
  // Panel slides out to the LEFT of the always-docked rail; rail stays at 72.
  const panelWidth = useDerivedValue(
    () => panelExpandedState.value * panelWidthValue.value,
    [],
  );
  const railWidth = useDerivedValue(
    () => railVisibleState.value * rightSidebarConfig.dimensions.rail,
    [],
  );
  const contentWidth = useDerivedValue(
    () => screenWidth - sidebarWidth.value - panelWidth.value - railWidth.value,
    [screenWidth],
  );
  // Height is set here (animated style), not via `h-full` className: NativeWind
  // class styles do not reliably apply to Animated.View, so the columns would
  // otherwise collapse to their content height (e.g. the rail to its icons).
  const sidebarStyle = useAnimatedStyle(() => ({
    width: sidebarWidth.value,
    height: '100%',
  }));
  const contentStyle = useAnimatedStyle(() => ({
    width: contentWidth.value,
    height: '100%',
  }));
  const panelColumnStyle = useAnimatedStyle(() => ({
    width: panelWidth.value,
    height: '100%',
    overflow: 'hidden',
  }));
  // Inner panel stays at full open width so its content does not reflow while
  // the outer (clipping) column animates open/closed.
  const panelInnerStyle = useAnimatedStyle(() => ({
    width: panelWidthValue.value,
    height: '100%',
  }));
  const railColumnStyle = useAnimatedStyle(() => ({
    width: railWidth.value,
    height: '100%',
    overflow: 'hidden',
  }));
  return (
    <View className="flex flex-row flex-1 flex-nowrap w-full h-full">
      <Animated.View style={sidebarStyle}>
        <Sidebar />
      </Animated.View>
      <Animated.View style={contentStyle} className="relative bg-bg">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-5 left-5 z-10 bg-surface web:hover:bg-surface-elevated rounded-lg"
          onPress={toggleSidebar}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
        >
          <Icon name="PanelLeft" color={iconColor} />
        </Button>
        {children}
      </Animated.View>
      <Animated.View
        style={panelColumnStyle}
        className="h-full overflow-hidden"
      >
        {/* Full-width panel clipped by the animating column, so nothing peeks
            while it slides open/closed. */}
        <Animated.View style={panelInnerStyle} className="h-full">
          {/* Background on a plain View — `bg-*` does not apply on Animated.View. */}
          <View className="h-full bg-surface">
            <RightSidebar.Outlet />
          </View>
        </Animated.View>
      </Animated.View>
      <Animated.View style={railColumnStyle} className="h-full overflow-hidden">
        {/* Fixed-width rail clipped by the animating column. */}
        <View
          className="h-full"
          style={{ width: rightSidebarConfig.dimensions.rail }}
        >
          <RightSidebar.Rail />
        </View>
      </Animated.View>
    </View>
  );
};

export const SidebarLayout = ({ children }: SidebarLayoutProps) => {
  const layout = useCurrentLayout();

  // On mobile (tabs layout), render children without the sidebars. The right
  // sidebar provider is still mounted so any `(sidebar)` route's
  // `useRightSidebar()` / `<RightSidebar>` stays valid (it just renders no
  // rail/panel here).
  if (layout === 'tabs') {
    return <RightSidebarLayoutProvider>{children}</RightSidebarLayoutProvider>;
  }

  return (
    <SidebarLayoutProvider>
      <RightSidebarLayoutProvider>
        <_SidebarLayout>{children}</_SidebarLayout>
      </RightSidebarLayoutProvider>
    </SidebarLayoutProvider>
  );
};
